import type { ThemeDto } from "@/lib/types";
import { brandCssText } from "@/lib/brand";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ColorMode = "light" | "dark";

interface ThemeContextValue {
  mode: ColorMode;
  toggleMode: () => void;
  setMode: (mode: ColorMode) => void;
  brand: ThemeDto;
  /** Swap the live palette without a reload — used when an admin applies a theme. */
  setBrand: (brand: ThemeDto) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

const STORAGE_KEY = "steptember-color-mode";
const STYLE_ID = "steptember-theme";

/**
 * The palette is written as a stylesheet rather than inline custom properties
 * because the surface tokens are scoped to `:root:not(.dark)` — inline styles
 * on the root element would out-rank the dark-mode overrides.
 */
function applyBrand(brand: ThemeDto) {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;

  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.append(style);
  }

  style.textContent = brandCssText(brand);
}

export function ThemeProvider({
  brand,
  children,
}: {
  brand: ThemeDto;
  children: ReactNode;
}) {
  const [mode, setModeState] = useState<ColorMode>("light");
  const [activeBrand, setBrand] = useState<ThemeDto>(brand);

  // Server-rendered palette wins on navigation, so track it.
  useEffect(() => {
    setBrand(brand);
  }, [brand]);

  // Light is the product default. Only an explicit saved choice may override it.
  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    setModeState(stored === "dark" ? "dark" : "light");
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    applyBrand(activeBrand);
  }, [activeBrand]);

  const setMode = useCallback((next: ColorMode) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === "dark" ? "light" : "dark");
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, toggleMode, setMode, brand: activeBrand, setBrand }),
    [mode, toggleMode, setMode, activeBrand, setBrand],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
